/**
 * @file Page de connexion du hub communautaire Ringo.
 * Fournit l'authentification par email/mot de passe, la vérification 2FA (R-0035),
 * et la connexion par passkey WebAuthn (R-0044). Redirige vers le tableau de bord en cas de succès.
 */

import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import TwoFactorChallenge from '../components/TwoFactorChallenge';
import WebAuthnLogin from '../components/WebAuthnLogin';
import { getErrorMessage } from '../lib/errorMessages';
import { buildAuthLink, getPostAuthRedirect } from '../lib/authRedirect';
import { APP_ROUTES } from '../lib/routes';

/**
 * Composant de la page de connexion.
 * Gère trois flux d'authentification :
 * 1. Email/mot de passe classique.
 * 2. Défi 2FA TOTP (si le backend retourne requires_2fa: true).
 * 3. Connexion par passkey WebAuthn.
 * @returns {JSX.Element}
 */
export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const { login, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = getPostAuthRedirect(location);
  const registerLink = buildAuthLink(APP_ROUTES.register, redirectTo === APP_ROUTES.dashboard ? '' : redirectTo);

  /** Soumet les identifiants à l'API et gère le flux 2FA si nécessaire. */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.requires_2fa) {
        setRequires2FA(true);
      } else {
        navigate(redirectTo, { replace: true });
      }
    } catch (e) {
      setError(getErrorMessage(e, t('login.error_fallback')));
    } finally {
      setLoading(false);
    }
  };

  /** Callback après succès de la vérification 2FA. */
  const handle2FASuccess = async () => {
    await refreshUser();
    navigate(redirectTo, { replace: true });
  };

  /** Callback après succès de la connexion WebAuthn. */
  const handleWebAuthnSuccess = async () => {
    await refreshUser();
    navigate(redirectTo, { replace: true });
  };

  /* ── Écran de vérification 2FA ── */
  if (requires2FA) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="login-2fa-page">
        <div className="w-full max-w-sm px-6">
          <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-foreground mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('login.two_factor_title')}
          </h1>
          <p className="text-sm text-muted-foreground mb-6">{t('login.two_factor_subtitle')}</p>
          <TwoFactorChallenge onSuccess={handle2FASuccess} />
        </div>
      </div>
    );
  }

  /* ── Écran de connexion classique ── */
  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="login-page">
      <div className="w-full max-w-sm px-6">
        <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-foreground mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('login.title')}
        </h1>
        <p className="text-sm text-muted-foreground mb-8">{t('login.subtitle')}</p>
        {error && (
          <div className="mb-4 p-3 text-sm text-red-600 border border-red-200 rounded-sm bg-red-50" data-testid="login-error">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('login.email')}</Label>
            <Input
              id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 rounded-sm border-input focus:ring-1 focus:ring-ring focus:border-ring"
              required data-testid="login-email-input" autoComplete="email"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('login.password')}</Label>
            <div className="relative">
              <Input
                id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 rounded-sm border-input focus:ring-1 focus:ring-ring focus:border-ring pr-10"
                required data-testid="login-password-input" autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(p => !p)}
                className="absolute right-2 top-1/2 -translate-y-1/2 mt-[3px] text-muted-foreground hover:text-foreground transition-colors"
                data-testid="login-toggle-password"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <Button type="submit" className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" disabled={loading} data-testid="login-submit-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('login.submit')}
          </Button>
        </form>

        {/* Séparateur pour les méthodes alternatives */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
          <div className="relative flex justify-center text-xs"><span className="bg-background px-2 text-muted-foreground">{t('login.or')}</span></div>
        </div>

        {/* Connexion par passkey (R-0044) */}
        <WebAuthnLogin onSuccess={handleWebAuthnSuccess} />

        <div className="mt-4 text-center">
          <Link to={APP_ROUTES.forgotPassword} className="text-sm text-muted-foreground hover:text-foreground hover:underline" data-testid="forgot-password-link">
            {t('login.forgot_password')}
          </Link>
        </div>
        <p className="mt-4 text-sm text-muted-foreground text-center">
          {t('login.no_account')}{' '}
          <Link to={registerLink} className="text-foreground font-medium hover:underline" data-testid="register-link">{t('login.create_one')}</Link>
        </p>
      </div>
    </div>
  );
}
